import React from 'react';
import { useState } from 'react';
import { Target, Palette, TrendingUp, Settings, LogOut, User, ArrowLeft, X, CreditCard, Home, Bell } from 'lucide-react';

interface NavigationProps {
  activeView: string;
  onViewChange: (view: string) => void;
  user: any;
}

const Navigation: React.FC<NavigationProps> = ({ activeView, onViewChange, user }) => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const menuItems = [
    { id: 'dashboard', label: 'Inicio', icon: Home, color: 'text-blue-600' },
    { id: 'campaigns', label: 'Campañas', icon: Target, color: 'text-green-600' },
    { id: 'creatives', label: 'Creativos', icon: Palette, color: 'text-purple-600' },
    { id: 'notifications', label: 'Notificaciones', icon: Bell, color: 'text-orange-600' },
  ];

  const menuModalItems = [
    { id: 'analytics', label: 'Analíticas', icon: TrendingUp, color: 'text-orange-600' },
    { id: 'billing', label: 'Facturación', icon: CreditCard, color: 'text-indigo-600' },
    { id: 'settings', label: 'Configuración', icon: Settings, color: 'text-gray-600' },
  ];

  const handleLogout = async () => {
    // Add logout logic here
    window.location.href = 'http://localhost:5173';
  };

  return (
    <>

      {/* Desktop Sidebar */}
      <div className="hidden lg:block fixed left-0 top-0 w-64 h-full bg-white border-r border-gray-200 z-40">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-gray-200">
            <div className="text-center">
              <img 
                src="https://soartistic.com/wp-content/uploads/2025/06/logo-negro.svg" 
                alt="SoArtistic" 
                className="h-12 w-auto object-contain mx-auto mb-2"
              />
              <p className="text-sm text-blue-600 font-bold">Ads Manager</p>
            </div>
          </div>

          {/* Back to SoArtistic Button */}
          <div className="p-4">
            <button
              data-tour="nav-back"
              onClick={() => window.location.href = 'https://app.soartistic.com'}
              className="w-full flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-md hover:shadow-lg transform hover:scale-105"
            >
              <ArrowLeft size={16} />
              <span className="font-medium text-sm">Volver a SoArtistic</span>
            </button>
          </div>

          {/* Navigation */}
          <div className="flex-1 overflow-y-auto p-4">
            <nav className="space-y-2">
              {[...menuItems, ...menuModalItems].map((item) => {
                const Icon = item.icon;
                const isActive = activeView === item.id;
                
                return (
                  <button
                    key={item.id}
                    data-tour={`nav-${item.id}`}
                    onClick={() => onViewChange(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                      isActive
                        ? 'bg-blue-50 text-blue-700 shadow-sm border border-blue-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon size={20} className={isActive ? 'text-blue-600' : item.color} />
                    <span className="font-medium">{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* User Profile */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="text-blue-600" size={16} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900 text-sm">{user?.name}</p>
                <p className="text-gray-500 text-xs">{user?.company}</p>
              </div>
            </div>
            
            <button data-tour="nav-logout" className="w-full flex items-center gap-3 px-4 py-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all">
              <LogOut size={16} />
              <span className="text-sm">Cerrar sesión</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Footer */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50">
        <div className="grid grid-cols-5 gap-1 py-2 px-2">
          {/* Main 4 items */}
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;
            
            return (
              <button
                key={item.id}
                data-tour={`nav-${item.id}`}
                onClick={() => onViewChange(item.id)}
                className={`flex flex-col items-center gap-1 py-2 px-1 rounded-lg transition-all touch-target text-center ${
                  isActive
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-600'
                }`}
              >
                <div className="relative">
                  <Icon size={18} />
                  {item.id === 'notifications' && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
                </div>
                <span className="text-xs font-medium leading-tight text-center" style={{ fontSize: '10px' }}>
                  {item.label}
                </span>
              </button>
            );
          })}
          
          {/* Menu button */}
          <button
            data-tour="nav-menu"
            onClick={() => setShowMobileMenu(true)}
            className="flex flex-col items-center gap-1 py-2 px-1 rounded-lg transition-all touch-target text-center text-gray-600 relative"
          >
            <div className="relative">
              <img 
                src="https://soartistic.com/wp-content/uploads/2025/06/logo-negro.svg" 
                alt="SoArtistic" 
                className="w-4 h-4 object-contain"
              />
            </div>
            <span className="text-xs font-medium leading-tight text-center" style={{ fontSize: '10px' }}>
              Menú
            </span>
          </button>
        </div>
      </div>

      {/* Mobile Menu Modal */}
      {showMobileMenu && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
          <div className="bg-white w-full max-h-[80vh] overflow-y-auto rounded-t-3xl">
            <div className="p-6">
              {/* Header with SoArtistic logo */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <img 
                    src="https://soartistic.com/wp-content/uploads/2025/06/logo-negro.svg" 
                    alt="SoArtistic" 
                    className="w-8 h-8 object-contain"
                  />
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">SoArtistic</h2>
                    <p className="text-sm text-gray-500">Ads Manager</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowMobileMenu(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X size={20} />
                </button>
              </div>

              {/* User Info */}
              <div className="flex items-center gap-3 mb-6 p-4 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="text-blue-600" size={20} />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{user?.email || 'Usuario'}</p>
                  <p className="text-sm text-gray-500">Cuenta Publicitaria</p>
                </div>
              </div>

              {/* Menu Items */}
              <div className="space-y-3 mb-6">
                {menuModalItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeView === item.id;
                  
                  return (
                    <button
                      key={item.id}
                      data-tour={`nav-${item.id}`}
                      onClick={() => {
                        onViewChange(item.id);
                        setShowMobileMenu(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        isActive
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      <Icon size={20} className={isActive ? 'text-blue-600' : item.color} />
                      <span className="font-medium">{item.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Actions */}
              <div className="pt-4 border-t border-gray-200 space-y-3">
                <button
                  onClick={() => window.location.href = 'https://app.soartistic.com'}
                  className="w-full flex items-center gap-3 px-4 py-3 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-xl transition-all"
                >
                  <ArrowLeft size={20} />
                  <span className="font-medium">Volver a SoArtistic</span>
                </button>
                
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl transition-all"
                >
                  <LogOut size={20} />
                  <span className="font-medium">Cerrar sesión</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;
